/**
 * The reason box, and the fact that it is required.
 *
 * Every destructive payment operation writes a permanent row against the operator's name, and
 * the reason is the only part of that row a person can read in three months. So the field is
 * not optional, the rule is stated before the operator types rather than after they submit,
 * and the message names what to change instead of saying "invalid".
 *
 * ONE COMPONENT, USED BY ALL FIVE DIALOGS, because the rule the server enforces is one rule.
 * Two copies of "at least ten characters and more than one word" would drift, and the copy
 * that drifted would be the one that told an operator their reason was fine.
 *
 * `problem` comes from `paymentOps.reasonProblem`, which mirrors `audit.validate_reason`.
 * The server checks it too, and — since `operations.refund` step 1 — checks it BEFORE the
 * Stripe call, so this is a courtesy to the operator rather than the only thing standing
 * between a blank reason and a moved RM.
 */

import { TextAreaField } from '../../shared/ui';
import { REASON_MIN_LENGTH, reasonProblem } from '../api/paymentOps';

export { reasonProblem, REASON_MIN_LENGTH };

export function ReasonField({
  value,
  onChange,
  /**
   * Shown only once the operator has tried to submit. A red message under an empty box the
   * moment a dialog opens tells somebody off for not having typed yet.
   */
  showProblem,
  label = 'Why are you doing this?',
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  showProblem: boolean;
  label?: string;
  disabled?: boolean;
}) {
  const problem = reasonProblem(value);
  return (
    <TextAreaField
      name="ops-reason"
      label={label}
      rows={3}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      hint={`Recorded permanently against your name. At least ${REASON_MIN_LENGTH} characters and more than one word.`}
      error={showProblem && problem ? problem : undefined}
    />
  );
}
